/* eslint-disable unicode-bom */
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  useTheme,
  Box,
  Typography,
  Divider,
  Menu,
  MenuItem,
  useMediaQuery,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import CommentRoundedIcon from "@mui/icons-material/CommentRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import Grid from "@mui/material/Grid2";
import { useParams } from "react-router-dom";
import api, { isAbortedRequestError } from "services/api";
import Tabs from "@mui/material/Tabs";
import { OVERLAY_NAMES } from "constants/Overlays";
import { a11yProps, CustomTabPanel } from "components/CustomTabPanel";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import CreditScoreRoundedIcon from "@mui/icons-material/CreditScoreRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CloudIcon from "@mui/icons-material/CloudRounded";
import TaskRoundedIcon from "@mui/icons-material/TaskRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import {
  formatAmount,
  formatDateLong,
  formatDateLongWithTime,
  getStatusColor,
} from "helpers/ServiceHelpers";
import { Card } from "components/Card";
import { Card2 } from "components/Card2";
import { NoteCard } from "components/NoteCard";
import { useStateContext } from "contexts/ContextProvider";
import dayjs from "dayjs";

import { useAuth } from "../../../contexts/AuthContext";
import CustomTab from "components/CustomTab";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import History from "../History";
import TaskItem from "components/TaskItem";
import { TaskBoard, buildTaskQueryParams } from "../../workspace/tasks/Tasks";
import { DragDropContext, Draggable } from "react-beautiful-dnd";
import { usePageTitle } from "hooks/usePageTitle";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { useWebSocket } from "contexts/WebSocketContext";
import { NAVBAR_HEIGHT } from "constants/layout";

const formatCurrencyValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "$0";
  }

  return `$${value}`;
};

const TranslationProfile = () => {
  const SERVICE_PROFILE_VIEW_KEY = "SERVICE_PROFILE_VIEW_MODE";
  const [pageTitle, setPageTitle] = useState("Translation");
  usePageTitle(pageTitle);
  const { currentUser } = useAuth();
  const canUseModernDetailsView = false;
  const { socket } = useWebSocket();

  const [tabValue, setTabValue] = useState(0);
  const [mobileTabValue, setMobileTabValue] = React.useState(0);
  const [useModernDetailsView] = useState(() => {
    try {
      if (!canUseModernDetailsView) return false;
      return localStorage.getItem(SERVICE_PROFILE_VIEW_KEY) === "modern";
    } catch (error) {
      console.error("Failed to read service profile view mode:", error);
      return false;
    }
  });
  const isModernDetailsEnabled =
    canUseModernDetailsView && useModernDetailsView;
  const showMobileOverview = !isModernDetailsEnabled;

  useEffect(() => {
    if (!canUseModernDetailsView) return;
    localStorage.setItem(
      SERVICE_PROFILE_VIEW_KEY,
      useModernDetailsView ? "modern" : "classic",
    );
  }, [canUseModernDetailsView, useModernDetailsView]);

  const overviewRef = useRef(null);
  const detailsRef = useRef(null);
  const tasksRef = useRef(null);
  const notesRef = useRef(null);
  const actionsRef = useRef(null);
  const historyRef = useRef(null);
  const mobileScrollRef = useRef(null);
  const mobileTabRef = useRef(0);
  const mobileSectionRefs = useMemo(
    () => [overviewRef, detailsRef, tasksRef, notesRef, actionsRef, historyRef],
    [],
  );

  const [taskStatuses, setTaskStatuses] = useState([]);
  const [tasksForService, setTasksForService] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);

  const handleTabsChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleMobileTabsChange = (event, newValue) => {
    setMobileTabValue(newValue);
    mobileTabRef.current = newValue;
    const sectionRef = mobileSectionRefs[
      newValue + (showMobileOverview ? 0 : 1)
    ];
    if (sectionRef?.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    const maxTabIndex = showMobileOverview ? 5 : 4;
    if (mobileTabRef.current > maxTabIndex) {
      mobileTabRef.current = maxTabIndex;
      setMobileTabValue(maxTabIndex);
    }
  }, [showMobileOverview]);

  const { id } = useParams();

  const location = useLocation();
  const data = location.state;
  const theme = useTheme();

  const {
    refreshPage,
    triggerPageRefresh,
    showSnackbar,
    openDialog,
    handleErrorDialog,
    closeDialog,
  } = useStateContext();
  const { setOverlayName, overlayData, setOverlayData } = useStateContext();

  const [listOfClients, setListOfClients] = useState([]);
  const [listOfLinguists, setListOfLinguists] = useState([]);
  const [listOfLanguages, setListOfLanguages] = useState([]);
  const [listOfStatuses, setListOfStatuses] = useState([]);
  const [listOfTypes, setListOfTypes] = useState([]);
  const [latestProjectNumber, setLatestProjectNumber] = useState(0);
  const [rateTypes, setRateTypes] = useState([]);
  const [deliveryTypes, setDeliveryTypes] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [serviceHistories, setServiceHistories] = useState([]);

  const [translationData, setTranslationData] = useState({});
  const [serviceCostSummary, setServiceCostSummary] = useState(null);
  const [showOtherCosts, setShowOtherCosts] = useState(false);

  // Track last update for snackbar notification
  const [lastUpdate, setLastUpdate] = useState(null);

  const getDataLists = useCallback(() => {
    api
      .all([
        api.get("/clients/brief"),
        api.get("/linguists/brief"),
        api.get("/languages"),
        api.get("/services/translations/statuses"),
        api.get("/services/translations/types"),
        api.get("/services/translations/latestProjectNumber"),
        api.get("/services/translations/rateTypes"),
        api.get("/services/translations/deliveryTypes"),
        api.get("/services/translations/paymentTypes"),
        api.get("/auditLogs/entity/Service/" + id),
      ])
      .then(
        api.spread(
          (
            clientsResponse,
            linguistsResponse,
            languagesResponse,
            statusesResponse,
            typesResponse,
            latestProjectNumberResponse,
            rateTypesResponse,
            deliveryTypesResponse,
            paymentTypesResponse,
            serviceHistoriesResponse,
          ) => {
            if (clientsResponse) {
              setListOfClients(clientsResponse.data);
            }
            if (linguistsResponse) {
              setListOfLinguists(linguistsResponse.data);
            }
            if (languagesResponse) {
              const tempLanguages = languagesResponse.data.filter(
                (language) => language.Name !== "ALL",
              );
              setListOfLanguages(tempLanguages);
            }
            if (statusesResponse) {
              setListOfStatuses(statusesResponse.data);
            }
            if (typesResponse) {
              setListOfTypes(typesResponse.data);
            }
            if (
              latestProjectNumberResponse &&
              latestProjectNumberResponse.data
            ) {
              setLatestProjectNumber(
                Number(latestProjectNumberResponse.data.ProjectNumber || 0) + 1,
              );
            }
            if (rateTypesResponse) {
              setRateTypes(rateTypesResponse.data);
            }
            if (deliveryTypesResponse) {
              const filteredDeliveryTypes = deliveryTypesResponse.data.filter(
                (type) => !["CODA", "SmartCat", "In-person"].includes(type),
              );
              setDeliveryTypes(filteredDeliveryTypes);
            }
            if (paymentTypesResponse) {
              setPaymentTypes(paymentTypesResponse.data);
            }
            if (serviceHistoriesResponse) {
              setServiceHistories(
                serviceHistoriesResponse.data?.auditLogs || [],
              );
            }
          },
        ),
      )
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.\nError: " +
            error.message;

        handleErrorDialog(message, theme);
      });
  }, [id, handleErrorDialog, theme]);

  const fetchServiceCostData = useCallback(() => {
    api
      .get(`/serviceCosts/service/${id}/lines`)
      .then((response) => {
        setServiceCostSummary(response.data?.summary || null);
      })
      .catch((error) => {
        console.error("Error fetching service cost lines:", error);
        setServiceCostSummary(null);
      });
  }, [id]);

  useEffect(() => {
    const fetchData = () => {
      api
        .get(`/services/translations/${id}`)
        .then((response) => {
          const translation = response.data;
          setPageTitle("Translation: " + translation.ProjectNumber);

          setTranslationData(translation);
          fetchServiceCostData();
          getDataLists();
        })
        .catch((error) => {
          console.error("Error fetching translation:", error);
          handleErrorDialog("Failed to load translation data", theme);
        });
    };
    fetchData();
  }, [
    id,
    refreshPage,
    getDataLists,
    fetchServiceCostData,
    handleErrorDialog,
    theme,
  ]);

  // Set entity tracking for presence and updates
  const { setCurrentEntity } = usePresence();
  usePagePresence(); // Join entity room when entity is set

  useEffect(() => {
    if (id) {
      setCurrentEntity({
        entityType: "Translation",
        entityId: parseInt(id),
      });
    }
    return () => {
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [id, setCurrentEntity]);

  // Listen for assignment updates from other members
  const handleAssignmentRefetch = useCallback(
    (updatedBy, meta = {}) => {
      // Reload the assignment data
      api
        .get(`/services/translations/${id}`)
        .then((response) => {
          setTranslationData(response.data);
          fetchServiceCostData();
          setLastUpdate({
            userName: updatedBy?.userName || "Another member",
            changeType: meta?.changeType,
            changeAction: meta?.changeAction,
            projectNumber: meta?.projectNumber,
          });
        })
        .catch((err) => console.log("Error refetching assignment:", err));
    },
    [id, fetchServiceCostData],
  );

  useAssignmentUpdates({
    modelType: "Translation",
    recordId: id ? parseInt(id) : null,
    onUpdate: handleAssignmentRefetch,
  });

  // Show snackbar when update is received (in separate effect to avoid listener churn)
  useEffect(() => {
    if (lastUpdate?.userName) {
      const action = lastUpdate.changeAction || "Updated";
      const changeType = lastUpdate.changeType || "Translation";
      const message = `${changeType} ${action} by ${lastUpdate.userName}. Check the History for more details`;
      showSnackbar(message, theme, "info");
      setLastUpdate(null);
    }
  }, [lastUpdate, showSnackbar, theme]);

  useEffect(() => {
    const fetchTaskEnums = async () => {
      try {
        const [statusesRes] = await Promise.all([
          api.get("/tasks/enums/statuses"),
        ]);
        setTaskStatuses(statusesRes.data || []);
      } catch (error) {
        console.error("Error fetching task enums:", error);
      }
    };

    fetchTaskEnums();
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const fetchTasksForService = async () => {
      if (!id) return;
      setTasksLoading(true);
      try {
        const params = buildTaskQueryParams(
          {
            entityServiceTypes: ["Translation"],
            showAllCompleted: true,
          },
          currentUser?.id,
        );
        params.append("entityId", id);

        const response = await api.get(`/tasks?${params.toString()}`, {
          signal: controller.signal,
        });
        setTasksForService(response.data || []);
      } catch (error) {
        if (isAbortedRequestError(error)) {
          return;
        }
        console.error("Error fetching tasks for service:", error);
        handleErrorDialog("Failed to load tasks", theme);
      } finally {
        if (!controller.signal.aborted) {
          setTasksLoading(false);
        }
      }
    };

    fetchTasksForService();

    return () => {
      controller.abort();
    };
  }, [id, refreshPage, currentUser?.id, handleErrorDialog, theme]);

  // Listen for task updates linked to this service
  useEffect(() => {
    if (!socket || !id) return;

    const handleTaskUpdate = async (data) => {
      // Check if task is linked to this service
      if (
        data.entityType === "Service" &&
        data.serviceType === "Translation" &&
        data.entityID === parseInt(id)
      ) {
        // Refetch tasks for this service
        try {
          const params = buildTaskQueryParams(
            {
              entityServiceTypes: ["Translation"],
              showAllCompleted: true,
            },
            currentUser?.id,
          );
          params.append("entityId", id);

          const response = await api.get(`/tasks?${params.toString()}`);
          setTasksForService(response.data || []);

          // Show snackbar notification
          const userName = data.updatedBy?.userName || "Unknown User";
          const message = `Task ${data.changeAction} by ${userName}`;
          showSnackbar(message, theme, "info");
        } catch (error) {
          if (!isAbortedRequestError(error)) {
            console.error("Error refreshing tasks:", error);
          }
        }
      }
    };

    socket.on("task:updated", handleTaskUpdate);
    return () => socket.off("task:updated", handleTaskUpdate);
  }, [socket, id, currentUser?.id, showSnackbar, theme]);

  const navigate = useNavigate();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (!isSmallScreen) return;

    const rootElement = mobileScrollRef.current;
    if (!rootElement) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visibleEntries.length > 0) {
          const activeIndex = Number(visibleEntries[0].target.dataset.index);
          const tabIndex = Math.max(
            activeIndex - (showMobileOverview ? 0 : 1),
            0,
          );
          if (
            !Number.isNaN(activeIndex) &&
            tabIndex !== mobileTabRef.current
          ) {
            mobileTabRef.current = tabIndex;
            setMobileTabValue(tabIndex);
          }
        }
      },
      {
        root: rootElement,
        rootMargin: "-88px 0px -60% 0px",
        threshold: [0.1, 0.25, 0.5, 0.75],
      },
    );

    mobileSectionRefs.forEach((sectionRef) => {
      if (sectionRef.current) {
        observer.observe(sectionRef.current);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [isSmallScreen, mobileSectionRefs, showMobileOverview]);

  const groupTasksByStatus = (taskList) => {
    const grouped = {};
    taskStatuses.forEach((status) => {
      grouped[status] = [];
    });

    taskList.forEach((task) => {
      if (!grouped[task.Status]) {
        grouped[task.Status] = [];
      }
      grouped[task.Status].push(task);
    });

    return grouped;
  };

  const flattenTasksByStatus = (groupedTasks) => {
    const ordered = [];

    taskStatuses.forEach((status) => {
      if (groupedTasks[status]) {
        ordered.push(...groupedTasks[status]);
      }
    });

    Object.keys(groupedTasks).forEach((status) => {
      if (!taskStatuses.includes(status)) {
        ordered.push(...groupedTasks[status]);
      }
    });

    return ordered;
  };

  const handleTaskDragEnd = async (result) => {
    const { destination, draggableId } = result;

    if (!destination) {
      return;
    }

    const newStatus = destination.droppableId;
    const taskId = draggableId.replace("task-", "");

    setTasksForService((prevTasks) => {
      const taskToMove = prevTasks.find(
        (task) => task.id.toString() === taskId,
      );
      if (!taskToMove) return prevTasks;

      const withoutTask = prevTasks.filter(
        (task) => task.id.toString() !== taskId,
      );
      const tasksByStatus = groupTasksByStatus(withoutTask);

      const updatedTask = { ...taskToMove, Status: newStatus };
      const destinationList = tasksByStatus[newStatus] || [];
      const insertAt = Math.min(destination.index, destinationList.length);
      destinationList.splice(insertAt, 0, updatedTask);
      tasksByStatus[newStatus] = destinationList;

      return flattenTasksByStatus(tasksByStatus);
    });

    try {
      await api.patch(`/tasks/updateStatus/${taskId}`, {
        Status: newStatus,
      });

      // Emit socket event for real-time updates
      if (socket) {
        const taskToUpdate = tasksForService.find(
          (t) => t.id.toString() === taskId,
        );
        if (taskToUpdate) {
          socket.emit("task:saved", {
            modelType: "Task",
            recordId: parseInt(taskId),
            entityType: taskToUpdate.EntityType,
            entityID: taskToUpdate.EntityID,
            serviceType: taskToUpdate.ServiceType,
            changeAction: "Updated",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }
      }
    } catch (error) {
      console.error("Error updating task status:", error);
      handleErrorDialog("Failed to update task status", theme);

      const params = buildTaskQueryParams(
        {
          entityServiceTypes: ["Interpretation"],
          showAllCompleted: true,
        },
        currentUser?.id,
      );
      params.append("entityId", id);
      const response = await api.get(`/tasks?${params.toString()}`);
      setTasksForService(response.data || []);
    }
  };

  const renderTaskCard = (task, index) => (
    <Draggable
      draggableId={`task-${task.id}`}
      index={index}
      key={task.id}
      disableInteractiveElementBlocking
    >
      {(provided, snapshot) => (
        <TaskItem
          key={task.id}
          task={task}
          onClick={() => {
            setOverlayData({ Task: task });
            setOverlayName(OVERLAY_NAMES.TASK_PROFILE);
          }}
          provided={provided}
          snapshot={snapshot}
        />
      )}
    </Draggable>
  );

  const languages = translationData.ServiceTargetLanguages;

  const targetLanguages =
    !languages || languages.length === 0
      ? "N/A"
      : languages
          .map((item) =>
            item.Language && item.Language.Name ? item.Language.Name : "",
          )
          .filter((name) => name)
          .join(" | ");
  const invoiceNumber = translationData.Invoice?.Number || null;
  const invoiceID = translationData.Invoice?.id || null;
  const hasLinkedInvoice = Boolean(invoiceID || translationData.InvoiceID);
  const canMarkAsPaid = Boolean(
    !translationData.Paid && !translationData.Invoiced,
  );
  const canMarkAsUnpaid = Boolean(translationData.Paid && !hasLinkedInvoice);

  const linguistName = translationData.Linguist?.Name?.trim() || "";
  const normalizedLinguistName = linguistName.toLowerCase();
  const isLinguistUnavailable =
    !linguistName ||
    normalizedLinguistName === "n/a" ||
    normalizedLinguistName === "na" ||
    normalizedLinguistName === "none" ||
    normalizedLinguistName === "null" ||
    normalizedLinguistName === "undefined" ||
    normalizedLinguistName === "-";

  const clientID = translationData.Client?.id || null;
  const linguistID = isLinguistUnavailable
    ? null
    : translationData.Linguist?.id || null;

  const listOfDetails = [
    {
      title: "Client Data",
      items: [
        {
          subtitle: "Client",
          content: translationData.Client?.Account || "N/A",
          onClick: clientID
            ? () =>
                navigate(`/clients/${clientID}`, {
                  state: {
                    ComingFrom: `/translations/${id}`,
                  },
                })
            : null,
        },
        {
          subtitle: "Requestor",
          content: translationData.Requestor?.Name || "N/A",
        },
        {
          subtitle: "Invoicer",
          content: translationData.Invoicer?.Name || "N/A",
        },
      ],
    },
    {
      title: "Linguist Data",
      items: [
        {
          subtitle: "Linguist",
          content: isLinguistUnavailable ? "N/A" : translationData.Linguist?.Name,
          onClick: linguistID
            ? () =>
                navigate(`/linguists/${linguistID}`, {
                  state: {
                    ComingFrom: `/translations/${id}`,
                  },
                })
            : null,
        },
        {
          subtitle: "Source Language",
          content: translationData.Language?.Name || "N/A",
        },
        {
          subtitle: "Rate",
          content: formatAmount(translationData.Rate) || "N/A",
        },
      ],
    },
    {
      title: translationData.UnitType?.Name || "Units",
      items: [
        {
          subtitle: "Unit Type",
          content: translationData.UnitType?.Name || "N/A",
        },
        {
          subtitle: "Unit Count",
          content:
            translationData.UnitCount || translationData.WordCount || "N/A",
        },
        {
          subtitle: "Number Of Languages",
          content: translationData.NumberOfLanguages,
        },
        {
          subtitle: "Total Unit Count",
          content:
            Number(
              translationData.UnitCount || translationData.WordCount || 0,
            ) * Number(translationData.NumberOfLanguages) || "N/A",
        },
      ],
    },
    {
      title: "Assignment",
      items: [
        { subtitle: "Name", content: translationData.Name || "N/A" },
        {
          subtitle: "Type",
          content: translationData.ServiceSubtype?.Name || "N/A",
        },
        {
          subtitle: "Delivery Date",
          content: translationData.DeliveryDate
            ? dayjs.tz(translationData.DeliveryDate).format("M/D/YYYY")
            : "N/A",
        },
      ],
    },
    {
      title: "More Details",
      items: [
        {
          subtitle: "Delivery Type",
          content: translationData.DeliveryType || "N/A",
        },
        {
          subtitle: "Payment Type",
          content: translationData.PaymentType || "N/A",
        },
        { subtitle: "Rate Type", content: translationData.RateType || "N/A" },
      ],
    },
    {
      title: "Statuses",
      items: [
        {
          subtitle: "Paid",
          content: (
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {translationData.Paid ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          ),
        },
        {
          subtitle: "Invoiced",
          content: (
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {translationData.Invoiced ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          ),
        },
        {
          subtitle: "Cancelled",
          content: (
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {translationData.Cancelled ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          ),
        },
      ],
    },
    {
      title: "Members",
      items: [
        {
          subtitle: "Created By",
          content: translationData.Creator?.Name || "N/A",
        },
        {
          subtitle: "Checked By",
          content: translationData.Checker?.Name || "N/A",
        },
        {
          subtitle: "Checked On",
          content: translationData.CheckingDate
            ? dayjs.tz(translationData.CheckingDate).format("M/D/YYYY h:mm A")
            : "N/A",
        },
      ],
    },
  ];

  if (translationData.CancellationReason) {
    listOfDetails.push({
      title: "Cancellation",
      items: [
        {
          subtitle: "",
          content: translationData.CancellationReason,
          multiline: true,
          centerContent: true,
        },
      ],
    });
  }

  const renderLegacyDetails = () => (
    <Grid container spacing={2}>
      {listOfDetails.map((detail, index) => (
        <Grid key={index} size={{ xs: 12, lg: 12, xl: 6 }}>
          <Card2 title={detail.title} items={detail.items} justify="start" />
        </Grid>
      ))}
    </Grid>
  );

  // NOTES MENU AND HANDLERS

  const [selectedServiceNote, setSelectedServiceNote] = useState(null);
  // Instead of using an anchor element, we now store the the mouse position for the context menu.
  const [contextMenuPosition, setContextMenuPosition] = useState(null);

  // Handler for editing using the existing logic.
  const handleEdit = (serviceNote) => {
    const data = {
      NoteID: serviceNote.id,
      Note: serviceNote.Note,
      ServiceID: translationData.id,
      MemberID: currentUser.id,
      ModelType: "Translation",
      Action: OVERLAY_NAMES.SERVICE_NOTE_ACTION_EDIT,
    };
    setOverlayData(data);
    setOverlayName(OVERLAY_NAMES.SERVICE_NOTE_NEW_EDIT);
  };

  // Handler for right-click to open context menu at the mouse position.
  const handleContextMenu = (e, serviceNote) => {
    e.preventDefault();
    setSelectedServiceNote(serviceNote);
    setContextMenuPosition({
      top: e.clientY,
      left: e.clientX,
    });
  };

  // Closes the context menu.
  const handleCloseContextMenu = () => {
    setContextMenuPosition(null);
  };

  // Stub for Delete action.
  const handleDelete = (serviceNote) => {
    openDialog({
      theme: theme,
      title: "Delete Note",
      message: "Are you sure you want to delete this note?",
      //TO DO ADD dialogIconDelete
      customContent: null,

      tertiaryAction: null,
      secondaryAction: "Close",
      primaryAction: "Delete",

      tertiaryOnClick: null,

      secondaryOnClick: () => {
        closeDialog();
      },

      primaryOnClick: () => {
        deleteNote();
        closeDialog();
      },
    });

    handleCloseContextMenu();
  };

  const markAssignmentAsChecked = () => {
    const data = {
      id: translationData.id,

      CheckedBy: currentUser.id,
      CheckingDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
    };

    api
      .post("/services/markServiceChecked", {
        data: data,
      })
      .then((response) => {
        triggerPageRefresh("Translation Assignment Marked as Checked", theme);

        closeDialog();
      })
      .catch((err) => console.log(err));
  };

  const markAssignmentAsPaid = () => {
    const data = {
      id: translationData.id,
      Paid: true,
      Invoiced: true,
    };

    api
      .post("/services/markServicePaid", {
        data: data,
      })
      .then((response) => {
        triggerPageRefresh(
          "Interpretation Assignment Marked as Checked",
          theme,
        );

        closeDialog();
      })
      .catch((err) => console.log(err));
  };

  const markAssignmentAsUnpaid = () => {
    const data = {
      id: translationData.id,
      Paid: false,
      Invoiced: false,
      InvoiceID: null,
    };

    api
      .post("/services/markServicePaid", {
        data: data,
      })
      .then((response) => {
        triggerPageRefresh("Assignment Marked as Unpaid", theme);

        closeDialog();
      })
      .catch((err) => console.log(err));
  };

  const deleteNote = () => {
    api
      .post("/services/updateNote", {
        data: {
          NoteID: selectedServiceNote.id,
          IsDeleted: 1,
        },
      })
      .then((response) => {
        if (socket && translationData?.id) {
          socket.emit("assignment:saved", {
            modelType: "Translation",
            recordId: translationData.id,
            changeType: "Note",
            changeAction: "Deleted",
            user: {
              id: currentUser?.id,
              name: currentUser?.Name || "User",
              imageThumbnail: currentUser?.ImageThumbnail,
            },
          });
        }
        triggerPageRefresh("Note Deleted Sucessfully", theme);
        closeDialog();
      })
      .catch((err) => console.log(err));
  };

  const deleteService = () => {
    const dataOld = {
      Assignment: {
        id: translationData.id,
        Deleted: 0,
      },
    };

    const dataNew = {
      Assignment: {
        id: translationData.id,
        Deleted: 1,
      },
    };

    const allData = {
      Old: dataOld,
      New: dataNew,
    };

    api
      .post("/services/deleteService", {
        data: allData,
      })
      .then((response) => {
        triggerPageRefresh("Translation Assignment Deleted Sucessfully", theme);

        const navBackTo =
          overlayData?.ComingFrom ??
          data?.ComingFrom ??
          `${window.location.origin}/translations`;

        navigate(`${navBackTo}`);
      })
      .catch((err) => console.log(err));
  };

  const formatTrackerDate = (value) => {
    if (!value) return "";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("M/D/YYYY") : "";
  };

  const sanitizeCell = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    return /["\t\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const buildTrackerRow = () => {
    const notesText = (translationData.ServiceNotes || [])
      .map((note) => note?.Note)
      .filter(Boolean)
      .join("\n");

    const normalizedMargin =
      translationData.Margin === null || translationData.Margin === undefined
        ? ""
        : Number(translationData.Margin) / 100;

    const notarizationCost = translationData.NotarizationOrCOTCost;
    const notarizedOrCot =
      notarizationCost && Number(notarizationCost) !== 0 ? "True" : "";

    const paidValue = translationData.Paid ? "True" : "";

    const formattedDeliveryDate = formatTrackerDate(
      translationData.DeliveryDate,
    );

    const privateNotes =
      translationData.PrivateNotes || translationData.PrivateNote || "";

    const columns = [
      formatTrackerDate(translationData.Date),
      formatTrackerDate(translationData.Deadline),
      translationData.ProjectNumber,
      translationData.Client?.Account,
      translationData.Requestor?.Name,
      translationData.TranslationStatus,
      translationData.AccountCharge,
      translationData.Details,
      translationData.Linguist?.Name,
      translationData.TotalCost,
      translationData.AdditionalCost,
      translationData.LinguistPay,
      translationData.Profit,
      normalizedMargin,
      translationData.Language?.Name,
      targetLanguages,
      "",
      notesText,
      translationData.UnitType?.Name || "N/A",
      translationData.UnitCount || translationData.WordCount || "N/A",
      translationData.NumberOfLanguages,
      "",
      translationData.Rate,
      notarizedOrCot,
      translationData.NotarizationOrCOTCost,
      translationData.DeliveryType,
      translationData.DeliveryCost,
      translationData.PaymentType,
      translationData.PaymentFees,
      formattedDeliveryDate,
      paidValue,
      translationData.ServiceSubtype?.Name,
      translationData.CancellationReason,
      privateNotes,
    ];

    return columns.map(sanitizeCell).join("\t");
  };

  const handleCopyTrackerRow = async () => {
    try {
      const trackerRow = buildTrackerRow();
      await navigator.clipboard.writeText(trackerRow);
      showSnackbar("Tracker Row copied successfully.", theme, "success");
    } catch (error) {
      console.error("Error copying tracker row:", error);
      handleErrorDialog("Failed to copy tracker row", theme);
    }
  };

  return (
    <Box
      //borderRadius="1.5rem"
      //m=".5rem 1.5rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`, // Take full viewport height minus margin -- DONT CHANGE THIS
      }}
    >
      <Box
        id="test"
        borderRadius="0 0 1.5rem 1.5rem"
        bgcolor={theme.palette.background.alt}
        p={isSmallScreen ? "0.5rem" : "0.5rem 1rem"}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          overflowY: isSmallScreen ? "hidden" : "auto",
          overflowX: "hidden",
          //maxHeight: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`, // Update to use viewport height instead of fixed percentage
        }}
      >
        {isSmallScreen ? (
          // ==================== MOBILE LAYOUT ====================
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: 0,
            }}
          >
            <Tabs
              value={mobileTabValue}
              onChange={handleMobileTabsChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                minHeight: "48px",
                bgcolor: theme.palette.background.alt,
                boxShadow: "0 6px 12px -10px rgba(0, 0, 0, 0.35)",
                flexShrink: 0,
              }}
            >
              {showMobileOverview ? <CustomTab label="Overview" /> : null}
              <CustomTab label="Details" />
              <CustomTab label="Tasks" />
              <CustomTab label="Notes" />
              <CustomTab label="Actions" />
              <CustomTab label="History" />
            </Tabs>

            <Box
              ref={mobileScrollRef}
              sx={{
                flex: 1,
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 4,
                minHeight: 0,
                overflowY: "auto",
              }}
            >
              {showMobileOverview ? <Box
                ref={overviewRef}
                data-index={0}
                sx={{ scrollMarginTop: "72px" }}
              >
                <Typography variant="h3" sx={{ mb: 2, fontWeight: "bold" }}>
                  Overview
                </Typography>
                <Grid container spacing={2}>
                  {!isModernDetailsEnabled && <Grid size={{ xs: 12 }}>
                    <Card
                      big
                      subtitle="Number"
                      content={translationData.ProjectNumber}
                      theme={theme}
                      onClick={() => {
                        navigator.clipboard.writeText(
                          translationData.ProjectNumber,
                        );
                        showSnackbar(
                          "Project number copied to clipboard.",
                          theme,
                          "info",
                        );
                      }}
                    />
                  </Grid>}
                  {!isModernDetailsEnabled && <Grid size={{ xs: 12, sm: 6 }}>
                    <Card
                      subtitle="Client"
                      content={translationData.Client?.Account || "N/A"}
                      theme={theme}
                    />
                  </Grid>}
                  {!isModernDetailsEnabled && <Grid size={{ xs: 12, sm: 6 }}>
                    <Card
                      subtitle="Date"
                      content={formatDateLong(translationData.Date)}
                      theme={theme}
                    />
                  </Grid>}
                  {!isModernDetailsEnabled && <Grid size={{ xs: 12, sm: 6 }}>
                    <Card
                      subtitle="Linguist"
                      content={isLinguistUnavailable ? "N/A" : translationData.Linguist?.Name}
                      color={
                        isLinguistUnavailable
                          ? theme.palette.grey[500]
                          : undefined
                      }
                      theme={theme}
                    />
                  </Grid>}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Card
                      subtitle="Language"
                      content={translationData.Language?.Name || "N/A"}
                      theme={theme}
                    />
                  </Grid>
                  {!isModernDetailsEnabled && <Grid size={{ xs: 12 }}>
                    <Card
                      big
                      subtitle="Status"
                      content={
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 1.25,
                            py: 0.25,
                            borderRadius: "8px",
                            backgroundColor: getStatusColor(
                              theme,
                              translationData.TranslationStatus || "N/A",
                            ),
                            color: theme.palette.background.default,
                            fontSize: "inherit",
                            fontWeight: 700,
                            lineHeight: 1.2,
                            textAlign: "center",
                          }}
                        >
                          {translationData.TranslationStatus || "N/A"}
                        </Box>
                      }
                      theme={theme}
                    />
                  </Grid>}
                  {!isModernDetailsEnabled && <Grid size={{ xs: 12, sm: 6 }}>
                    <Card
                      subtitle="Revenue"
                      content={formatCurrencyValue(translationData.AccountCharge)}
                      theme={theme}
                    />
                  </Grid>}
                  {!isModernDetailsEnabled && <Grid size={{ xs: 12, sm: 6 }}>
                    <Card
                      subtitle="Profit"
                      content={formatCurrencyValue(translationData.Profit)}
                      theme={theme}
                    />
                  </Grid>}
                </Grid>
              </Box> : null}

              <Box
                ref={detailsRef}
                data-index={1}
                sx={{ scrollMarginTop: "72px" }}
              >
                <Typography variant="h3" sx={{ mb: 2, fontWeight: "bold" }}>
                  Details
                </Typography>
                {renderLegacyDetails()}
              </Box>

              <Box
                ref={tasksRef}
                data-index={2}
                sx={{ scrollMarginTop: "72px" }}
              >
                <Typography variant="h3" sx={{ mb: 2, fontWeight: "bold" }}>
                  Tasks
                </Typography>
                <DragDropContext onDragEnd={handleTaskDragEnd}>
                  <TaskBoard
                    statuses={taskStatuses}
                    tasks={tasksForService}
                    isLoading={tasksLoading}
                    onDragEnd={handleTaskDragEnd}
                    renderTaskCard={renderTaskCard}
                    creatingTaskForStatus={null}
                    newTaskTitle=""
                    setNewTaskTitle={() => {}}
                    handleCreateInlineTask={() => {}}
                    setCreatingTaskForStatus={() => {}}
                    enableInlineCreate={false}
                    columnsOverride={1}
                    theme={theme}
                    heightFraction={1}
                    rowHeightFraction={0.8}
                    containerPadding={"0.25rem"}
                    containerMargin={null}
                    gridSpacing={2}
                    externalDragContext={true}
                  />
                </DragDropContext>
              </Box>

              <Box
                ref={notesRef}
                data-index={3}
                sx={{ scrollMarginTop: "72px" }}
              >
                <Typography
                  variant="h3"
                  sx={{ mb: 2, mt: 3, fontWeight: "bold" }}
                >
                  Notes
                </Typography>
                {translationData &&
                (translationData.ServiceNotes || []).length > 0 ? (
                  <>
                    <Grid container spacing={2}>
                      {(translationData.ServiceNotes || []).map(
                        (serviceNote, index) => (
                          <Grid
                            key={serviceNote.Note + "//" + index}
                            size={{ xs: 12 }}
                            onContextMenu={(e) =>
                              handleContextMenu(e, serviceNote)
                            }
                            sx={{ cursor: "pointer" }}
                          >
                            <NoteCard
                              title={serviceNote.Note}
                              items={
                                serviceNote.CreatedOn
                                  ? serviceNote.Member?.Name +
                                    " - " +
                                    formatDateLongWithTime(
                                      serviceNote.CreatedOn,
                                    )
                                  : serviceNote.Member?.Name
                              }
                              justify="start"
                            />
                          </Grid>
                        ),
                      )}
                    </Grid>
                    <Menu
                      open={Boolean(contextMenuPosition)}
                      onClose={handleCloseContextMenu}
                      anchorReference="anchorPosition"
                      anchorPosition={
                        contextMenuPosition
                          ? {
                              top: contextMenuPosition.top,
                              left: contextMenuPosition.left,
                            }
                          : undefined
                      }
                      slotProps={{
                        paper: {
                          sx: {
                            bgcolor: theme.palette.background.alt,
                            border: `1px solid ${theme.palette.divider}`,
                            "& .MuiListItemIcon-root": {
                              color: theme.palette.primary.main,
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          handleEdit(selectedServiceNote);
                          handleCloseContextMenu();
                        }}
                      >
                        <ListItemIcon>
                          <EditRoundedIcon fontSize="small" />
                        </ListItemIcon>
                        <Typography variant="inherit">Edit</Typography>
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleDelete(selectedServiceNote)}
                      >
                        <ListItemIcon>
                          <DeleteRoundedIcon fontSize="small" />
                        </ListItemIcon>
                        <Typography variant="inherit">Delete</Typography>
                      </MenuItem>
                    </Menu>
                  </>
                ) : (
                  <Box sx={{ textAlign: "center", p: 4 }}>
                    <Typography variant="h4">
                      There are no notes on this assignment, add one by clicking
                      the (+) button
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box
                ref={actionsRef}
                data-index={4}
                sx={{ scrollMarginTop: "72px" }}
              >
                <Typography variant="h3" sx={{ mb: 2, fontWeight: "bold" }}>
                  Actions
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<EditRoundedIcon />}
                    onClick={() => {
                      const secondData = {
                        ...translationData,
                        Action: OVERLAY_NAMES.TRANSLATION_ACTION_EDIT,
                        Clients: listOfClients,
                        Linguists: listOfLinguists,
                        Languages: listOfLanguages,
                        TranslationStatuses: listOfStatuses,
                        TranslationTypes: listOfTypes,
                        ProjectNumber: translationData.ProjectNumber,
                        RateTypes: rateTypes,
                        DeliveryTypes: deliveryTypes,
                        PaymentTypes: paymentTypes,
                      };
                      setOverlayData(secondData);
                      setOverlayName(OVERLAY_NAMES.TRANSLATION_NEW_EDIT);
                    }}
                    fullWidth
                    sx={{ borderRadius: "8px" }}
                  >
                    Edit Assignment
                  </Button>

                  {translationData.TranslationStatus &&
                    !(
                      translationData.TranslationStatus.includes("Completed") ||
                      translationData.TranslationStatus.includes("Cancelled")
                    ) && (
                      <Button
                        variant="outlined"
                        startIcon={<TaskRoundedIcon />}
                        onClick={() => {
                          const data = {
                            ...translationData,
                            Action: OVERLAY_NAMES.TRANSLATION_ACTION_COMPLETE,
                            Clients: listOfClients,
                            Linguists: listOfLinguists,
                            Languages: listOfLanguages,
                            TranslationStatuses: listOfStatuses,
                            TranslationTypes: listOfTypes,
                            ProjectNumber: latestProjectNumber,
                            RateTypes: rateTypes,
                            DeliveryTypes: deliveryTypes,
                            PaymentTypes: paymentTypes,
                          };

                          setOverlayData(data);
                          setOverlayName(OVERLAY_NAMES.TRANSLATION_NEW_EDIT);
                        }}
                        fullWidth
                        sx={{ borderRadius: "8px" }}
                      >
                        Complete Assignment
                      </Button>
                    )}

                  <Button
                    variant="outlined"
                    startIcon={<VerifiedUserRoundedIcon />}
                    onClick={() => {
                      openDialog({
                        theme: theme,
                        title: "Mark As Checked",
                        message:
                          "This assignment will be marked as Checked by you on today's date",
                        customContent: null,
                        tertiaryAction: null,
                        secondaryAction: "Close",
                        primaryAction: "Confirm",
                        tertiaryOnClick: null,
                        secondaryOnClick: () => {
                          closeDialog();
                        },
                        primaryOnClick: () => {
                          markAssignmentAsChecked();
                          closeDialog();
                        },
                      });
                    }}
                    fullWidth
                    sx={{ borderRadius: "8px" }}
                  >
                    Mark As Checked
                  </Button>

                  {canMarkAsPaid ? (
                    <Button
                      variant="outlined"
                      startIcon={<CreditScoreRoundedIcon />}
                      onClick={() => {
                        openDialog({
                          theme: theme,
                          title: "Mark As Paid",
                          message:
                            translationData.Client?.Account !==
                            "Private Persons"
                              ? "This assignment will be marked as Paid, and won't be invoiced, are you sure you want to continue?"
                              : "This assignment will be marked as Paid",
                          customContent: null,
                          tertiaryAction: null,
                          secondaryAction: "Close",
                          primaryAction: "Confirm",
                          tertiaryOnClick: null,
                          secondaryOnClick: () => {
                            closeDialog();
                          },
                          primaryOnClick: () => {
                            markAssignmentAsPaid();
                            closeDialog();
                          },
                        });
                      }}
                      fullWidth
                      sx={{ borderRadius: "8px" }}
                    >
                      Mark As Paid
                    </Button>
                  ) : canMarkAsUnpaid ? (
                    <Button
                      variant="outlined"
                      startIcon={<RestartAltRoundedIcon />}
                      onClick={() => {
                        openDialog({
                          theme: theme,
                          title: "Mark As Unpaid",
                          message:
                            "This assignment will be marked as Unpaid and returned to not invoiced status, are you sure you want to continue?",
                          customContent: null,
                          tertiaryAction: null,
                          secondaryAction: "Close",
                          primaryAction: "Confirm",
                          tertiaryOnClick: null,
                          secondaryOnClick: () => {
                            closeDialog();
                          },
                          primaryOnClick: () => {
                            markAssignmentAsUnpaid();
                            closeDialog();
                          },
                        });
                      }}
                      fullWidth
                      sx={{ borderRadius: "8px" }}
                    >
                      Mark As Unpaid
                    </Button>
                  ) : null}

                  <Button
                    variant="outlined"
                    startIcon={<CategoryRoundedIcon />}
                    onClick={() => {
                      const secondData = {
                        ServiceID: translationData.id,
                        Category: translationData.ServiceCategory,
                      };
                      setOverlayData(secondData);
                      setOverlayName(OVERLAY_NAMES.SERVICE_CHANGE_CATEGORY);
                    }}
                    fullWidth
                    sx={{ borderRadius: "8px" }}
                  >
                    Change Category
                  </Button>

                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteRoundedIcon />}
                    onClick={() => {
                      openDialog({
                        theme: theme,
                        title: "Delete Assignment",
                        message:
                          "Do you really wish to delete this assignment?",
                        customContent: null,
                        tertiaryAction: null,
                        secondaryAction: "Close",
                        primaryAction: "Delete",
                        tertiaryOnClick: null,
                        secondaryOnClick: () => {
                          closeDialog();
                        },
                        primaryOnClick: () => {
                          deleteService();
                          closeDialog();
                        },
                      });
                    }}
                    fullWidth
                    sx={{ borderRadius: "8px" }}
                  >
                    Delete Assignment
                  </Button>

                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Tasks & Notes
                  </Typography>

                  <Button
                    variant="outlined"
                    startIcon={<TaskRoundedIcon />}
                    onClick={() => {
                      setOverlayData({
                        Action: OVERLAY_NAMES.TASK_ACTION_NEW,
                        Task: {
                          EntityType: "Service",
                          EntityID: id,
                          ServiceType: "Translation",
                        },
                        Service: {
                          id: translationData.id,
                          ProjectNumber: translationData.ProjectNumber,
                          Details: translationData.Details,
                          ServiceCategory: translationData.ServiceCategory,
                        },
                      });
                      setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
                    }}
                    fullWidth
                    sx={{ borderRadius: "8px" }}
                  >
                    Add Task
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<CommentRoundedIcon />}
                    onClick={() => {
                      const data = {
                        ServiceID: translationData.id,
                        MemberID: currentUser.id,
                        ModelType: "Translation",
                        Action: OVERLAY_NAMES.SERVICE_NOTE_ACTION_NEW,
                      };
                      setOverlayData(data);
                      setOverlayName(OVERLAY_NAMES.SERVICE_NOTE_NEW_EDIT);
                    }}
                    fullWidth
                    sx={{ borderRadius: "8px" }}
                  >
                    Add Note
                  </Button>

                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Utilities
                  </Typography>

                  <Button
                    variant="outlined"
                    startIcon={<ContentCopyRoundedIcon />}
                    onClick={handleCopyTrackerRow}
                    fullWidth
                    sx={{ borderRadius: "8px" }}
                  >
                    Copy Tracker Row
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<CloudIcon />}
                    onClick={() => {
                      const year = dayjs(translationData.Date).format("YYYY");
                      const oneDriveLink =
                        "https://communicationessentialsllc-my.sharepoint.com/personal/amer_communicationessentialsllc_com/_layouts/15/onedrive.aspx?id=%2Fpersonal%2Famer%5Fcommunicationessentialsllc%5Fcom%2FDocuments%2FCommunication%20Essentials%2FAssignments%2FTranslations%20" +
                        year +
                        "%2F" +
                        translationData.ProjectNumber;
                      window.open(oneDriveLink, "_blank");
                    }}
                    fullWidth
                    sx={{ borderRadius: "8px" }}
                  >
                    Open One Drive Link
                  </Button>
                </Box>
              </Box>

              <Box
                ref={historyRef}
                data-index={5}
                sx={{ scrollMarginTop: "72px" }}
              >
                <Typography variant="h3" sx={{ mb: 2, fontWeight: "bold" }}>
                  History
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <History serviceHistories={serviceHistories} />
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          // ==================== DESKTOP LAYOUT ====================
          <Grid
            container
            spacing={2}
            sx={{
              flex: 1,
              width: "100%",
              height: "100%",
              minHeight: 0,
            }}
          >
            {/* LEFT COLUMN - General Information */}
            <Grid
              size={{ xs: 12, md: "auto" }}
              sx={{
                display: isModernDetailsEnabled ? "none" : "flex",
                flexDirection: "column",
                minWidth: "250px",
                width: { md: "300px" },
                maxWidth: { md: "300px" },
                flexShrink: 0,
                height: "100%",
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  overflowY: "auto",
                  flex: 1,
                  width: "100%",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    flexGrow: 1,
                    p: ".75rem 0",
                    overflowY: "auto",
                    height: "100%",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="h4" fontWeight="bold">
                        General Information
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    <Grid container size={{ xs: 12 }} spacing={1}>
                      <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                        <Card
                          big
                          subtitle="Number"
                          content={translationData.ProjectNumber}
                          theme={theme}
                          onClick={() => {
                            navigator.clipboard.writeText(
                              translationData.ProjectNumber,
                            );
                            showSnackbar(
                              "Project number copied to clipboard.",
                              theme,
                              "info",
                            );
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                        <Card
                          big
                          subtitle="Status"
                          content={
                            <Box
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 1.25,
                                py: 0.25,
                                borderRadius: "8px",
                                backgroundColor: getStatusColor(
                                  theme,
                                  translationData.TranslationStatus || "N/A",
                                ),
                                color: theme.palette.background.default,
                                fontSize: "inherit",
                                fontWeight: 700,
                                lineHeight: 1.2,
                                textAlign: "center",
                              }}
                            >
                              {translationData.TranslationStatus || "N/A"}
                            </Box>
                          }
                          theme={theme}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }} container spacing={1} p=".25rem">
                        <Card
                          big
                          subtitle="Date"
                          content={formatDateLong(translationData.Date)}
                          theme={theme}
                        />
                      </Grid>
                      {translationData.Deadline ? (
                        <Grid size={{ xs: 12 }} container spacing={1} p=".25rem">
                          <Card
                            big
                            subtitle="Deadline"
                            content={formatDateLong(translationData.Deadline)}
                            theme={theme}
                          />
                        </Grid>
                      ) : null}
                      <Grid size={{ xs: 12 }} container spacing={1} p=".25rem">
                        <Card
                          big
                          subtitle="Invoice"
                          content={
                            translationData.Invoice?.Number ||
                            (translationData.Invoiced
                              ? translationData.Client?.Account ===
                                "Private Persons"
                                ? "Paid Cash"
                                : "Invoiced: Tracker"
                              : "Not Invoiced")
                          }
                          theme={theme}
                          onClick={
                            invoiceNumber
                              ? () =>
                                  navigate(`/invoices/${invoiceID}`, {
                                    state: {
                                      ComingFrom: `/otherServices/${id}`,
                                    },
                                  })
                              : null
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }} container spacing={1} p=".25rem">
                        <Card
                          big
                          subtitle="Target Languages"
                          content={targetLanguages}
                          theme={theme}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }} container spacing={1} p=".25rem">
                        <Card
                          big
                          subtitle="Account Charge"
                          content={
                            formatCurrencyValue(
                              translationData.AccountCharge,
                            )
                          }
                          theme={theme}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                        <Card
                          subtitle="Linguist Pay"
                          content={
                            formatCurrencyValue(
                              serviceCostSummary?.componentBreakdown
                                ?.LinguistPay ?? translationData.LinguistPay,
                            )
                          }
                          theme={theme}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                        <Card
                          big
                          subtitle="Total Cost"
                          content={
                            formatCurrencyValue(
                              serviceCostSummary?.totalCostFromLines ??
                                translationData.TotalCost,
                            )
                          }
                          theme={theme}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                        <Card
                          subtitle="Profit"
                          content={formatCurrencyValue(translationData.Profit)}
                          theme={theme}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                        <Card
                          subtitle="Margin"
                          content={
                            translationData.Margin === 0
                              ? "0%"
                              : translationData.Margin === 100
                                ? "100%"
                                : `${translationData.Margin}%`
                          }
                          theme={theme}
                          color={
                            translationData.Margin < 20
                              ? theme.palette.error.main
                              : translationData.Margin < 40
                                ? theme.palette.warning.main
                                : theme.palette.success.main
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }} sx={{ mt: 2, mb: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            cursor: "pointer",
                            userSelect: "none",
                            "&:hover": { opacity: 0.8 },
                          }}
                          onClick={() => setShowOtherCosts(!showOtherCosts)}
                        >
                          {showOtherCosts
                            ? <KeyboardArrowDownRoundedIcon fontSize="small" />
                            : <KeyboardArrowRightRoundedIcon fontSize="small" />}
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: "0.95rem" }}
                          >
                            Other Costs
                          </Typography>
                        </Box>
                      </Grid>
                      {showOtherCosts && (
                        <>
                          <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                            <Card
                              subtitle="Additional Cost"
                              content={formatAmount(
                                serviceCostSummary?.componentBreakdown?.AdditionalCost ||
                                translationData.AdditionalCost
                              )}
                              theme={theme}
                            />
                          </Grid>
                          <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                            <Card
                              subtitle="Payment Fees"
                              content={formatAmount(
                                serviceCostSummary?.componentBreakdown?.PaymentFees ||
                                translationData.PaymentFees
                              )}
                              theme={theme}
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ display: "block" }}
            />

            {/* MIDDLE COLUMN - Details/Tasks/Notes/History */}
            <Grid
              size={{ xs: 12, md: "grow" }}
              sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                flex: "1 1 auto",
                height: "100%",
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabsChange}
                    aria-label="basic tabs example"
                    sx={{ bgcolor: theme.palette.background.alt }}
                  >
                    <CustomTab label="Details" {...a11yProps(0)} />
                    <CustomTab label="Tasks" {...a11yProps(1)} />
                    <CustomTab label="Notes" {...a11yProps(2)} />
                    <CustomTab label="History" {...a11yProps(3)} />
                  </Tabs>
                </Box>
                <DragDropContext onDragEnd={handleTaskDragEnd}>
                  <CustomTabPanel
                    value={tabValue}
                    index={0}
                    key="Tab One"
                    sx={{ flex: 1, display: "flex", flexDirection: "column" }}
                  >
                    <Box
                      sx={{
                        flexGrow: 1,
                        p: ".75rem 0.25rem",
                        overflowY: "auto",
                        height: "100%",
                      }}
                    >
                      {renderLegacyDetails()}
                    </Box>
                  </CustomTabPanel>
                  <CustomTabPanel
                    value={tabValue}
                    index={1}
                    key="Tab Two"
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "visible",
                      minHeight: 0,
                    }}
                  >
                    <TaskBoard
                      statuses={taskStatuses}
                      tasks={tasksForService}
                      isLoading={tasksLoading}
                      onDragEnd={handleTaskDragEnd}
                      renderTaskCard={renderTaskCard}
                      creatingTaskForStatus={null}
                      newTaskTitle=""
                      setNewTaskTitle={() => {}}
                      handleCreateInlineTask={() => {}}
                      setCreatingTaskForStatus={() => {}}
                      enableInlineCreate={false}
                      columnsOverride={2}
                      theme={theme}
                      heightFraction={1}
                      rowHeightFraction={0.5}
                      containerPadding={"0.25rem"}
                      containerMargin="0"
                      gridSpacing={2}
                      externalDragContext={true}
                    />
                  </CustomTabPanel>
                  <CustomTabPanel
                    value={tabValue}
                    index={2}
                    key="Tab Three"
                    sx={{ flex: 1, display: "flex", flexDirection: "column" }}
                  >
                    {translationData &&
                    (translationData.ServiceNotes || []).length > 0 ? (
                      <Box
                        sx={{
                          flexGrow: 1,
                          p: ".75rem 0.25rem",
                          overflowY: "auto",
                          height: "100%",
                        }}
                      >
                        <Grid container spacing={2}>
                          {(translationData.ServiceNotes || []).map(
                            (serviceNote, index) => (
                              <Grid
                                key={serviceNote.Note + "//" + index}
                                size={{ xs: 12 }}
                                onContextMenu={(e) =>
                                  handleContextMenu(e, serviceNote)
                                }
                                sx={{ cursor: "pointer" }}
                              >
                                <NoteCard
                                  title={serviceNote.Note}
                                  items={
                                    serviceNote.CreatedOn
                                      ? (serviceNote?.Member?.Name || "N/A") +
                                        " - " +
                                        formatDateLongWithTime(
                                          serviceNote.CreatedOn,
                                        )
                                      : serviceNote?.Member?.Name || "N/A"
                                  }
                                  justify="start"
                                />
                              </Grid>
                            ),
                          )}
                        </Grid>
                        <Menu
                          open={Boolean(contextMenuPosition)}
                          onClose={handleCloseContextMenu}
                          anchorReference="anchorPosition"
                          anchorPosition={
                            contextMenuPosition
                              ? {
                                  top: contextMenuPosition.top,
                                  left: contextMenuPosition.left,
                                }
                              : undefined
                          }
                          slotProps={{
                            paper: {
                              sx: {
                                bgcolor: theme.palette.background.alt,
                                border: `1px solid ${theme.palette.divider}`,
                                "& .MuiListItemIcon-root": {
                                  color: theme.palette.primary.main,
                                },
                              },
                            },
                          }}
                        >
                          <MenuItem
                            onClick={() => {
                              handleEdit(selectedServiceNote);
                              handleCloseContextMenu();
                            }}
                          >
                            <ListItemIcon>
                              <EditRoundedIcon fontSize="small" />
                            </ListItemIcon>
                            <Typography variant="inherit">Edit</Typography>
                          </MenuItem>
                          <MenuItem
                            onClick={() => handleDelete(selectedServiceNote)}
                          >
                            <ListItemIcon>
                              <DeleteRoundedIcon fontSize="small" />
                            </ListItemIcon>
                            <Typography variant="inherit">Delete</Typography>
                          </MenuItem>
                        </Menu>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "80%",
                          width: "100%",
                        }}
                      >
                        <Typography variant="h4">
                          There are no notes on this assignment, add one by
                          clicking the (+) button
                        </Typography>
                      </Box>
                    )}
                  </CustomTabPanel>
                  <CustomTabPanel
                    value={tabValue}
                    index={3}
                    key="Tab Four"
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      p: 0,
                    }}
                  >
                    <Box
                      sx={{
                        overflowY: "auto",
                        height: "100%",
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <History serviceHistories={serviceHistories} />
                    </Box>
                  </CustomTabPanel>
                </DragDropContext>
              </Box>
            </Grid>

            <Divider orientation="vertical" flexItem />

            {/* RIGHT COLUMN - Actions */}
            <Grid
              size={{ xs: 12, md: "auto" }}
              sx={{
                display: "flex",
                flexDirection: "column",
                width: { md: "300px" },
                minWidth: { md: "300px" },
                maxWidth: { md: "300px" },
                flexShrink: 0,
                height: "100%",
              }}
            >
              <Box
                sx={{
                  overflowY: "auto",
                  flex: 1,
                  width: "100%",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    flexGrow: 1,
                    p: ".75rem 0",
                    overflowY: "auto",
                    height: "100%",
                  }}
                >
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="h5" fontWeight="bold">
                      Actions
                    </Typography>
                  </Grid>

                  <Divider
                    sx={{
                      my: 2,
                    }}
                  />
                  <Box width="100%">
                    <Typography
                      sx={{
                        m: "0.25rem 0 0 0.5rem",
                        color: theme.palette.primary.main,
                        fontSize: 13,
                      }}
                    >
                      Assignment
                    </Typography>

                    <ListItem key="Edit" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          const secondData = {
                            ...translationData,
                            Action: OVERLAY_NAMES.TRANSLATION_ACTION_EDIT,
                            Clients: listOfClients,
                            Linguists: listOfLinguists,
                            Languages: listOfLanguages,
                            TranslationStatuses: listOfStatuses,
                            TranslationTypes: listOfTypes,
                            ProjectNumber: translationData.ProjectNumber,
                            RateTypes: rateTypes,
                            DeliveryTypes: deliveryTypes,
                            PaymentTypes: paymentTypes,
                          };
                          setOverlayData(secondData);
                          setOverlayName(OVERLAY_NAMES.TRANSLATION_NEW_EDIT);
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <EditRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Edit Assignment" />
                      </ListItemButton>
                    </ListItem>

                    {translationData.TranslationStatus &&
                    (translationData.TranslationStatus.includes("Completed") ||
                      translationData.TranslationStatus.includes(
                        "Cancelled",
                      )) ? null : (
                      <ListItem key="Complete" disablePadding>
                        <ListItemButton
                          sx={{
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                          onClick={() => {
                            const data = {
                              ...translationData,
                              Action: OVERLAY_NAMES.TRANSLATION_ACTION_COMPLETE,
                              Clients: listOfClients,
                              Linguists: listOfLinguists,
                              Languages: listOfLanguages,
                              TranslationStatuses: listOfStatuses,
                              TranslationTypes: listOfTypes,
                              ProjectNumber: latestProjectNumber,
                              RateTypes: rateTypes,
                              DeliveryTypes: deliveryTypes,
                              PaymentTypes: paymentTypes,
                            };

                            setOverlayData(data);
                            setOverlayName(OVERLAY_NAMES.TRANSLATION_NEW_EDIT);
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <TaskRoundedIcon
                              sx={{
                                fontSize: 28,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Complete Assignment" />
                        </ListItemButton>
                      </ListItem>
                    )}

                    <ListItem key="Check" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          openDialog({
                            theme: theme,
                            title: "Mark As Checked",
                            message:
                              "This assignment will be marked as Checked by you on today's date",
                            customContent: null,

                            tertiaryAction: null,
                            secondaryAction: "Close",
                            primaryAction: "Confirm",

                            tertiaryOnClick: null,

                            secondaryOnClick: () => {
                              closeDialog();
                            },

                            primaryOnClick: () => {
                              markAssignmentAsChecked();
                              closeDialog();
                            },
                          });
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <VerifiedUserRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Mark As Checked" />
                      </ListItemButton>
                    </ListItem>

                    {canMarkAsPaid ? (
                      <ListItem key="Pay" disablePadding>
                        <ListItemButton
                          sx={{
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                          onClick={() => {
                            openDialog({
                              theme: theme,
                              title: "Mark As Paid",
                              message:
                                translationData.Client?.Account !==
                                "Private Persons"
                                  ? "This assignment will be marked as Paid, and won't be invoiced, are you sure you want to continue?"
                                  : "This assignment will be marked as Paid",
                              customContent: null,

                              tertiaryAction: null,
                              secondaryAction: "Close",
                              primaryAction: "Confirm",

                              tertiaryOnClick: null,

                              secondaryOnClick: () => {
                                closeDialog();
                              },

                              primaryOnClick: () => {
                                markAssignmentAsPaid();
                                closeDialog();
                              },
                            });
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <CreditScoreRoundedIcon
                              sx={{
                                fontSize: 28,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Mark As Paid" />
                        </ListItemButton>
                      </ListItem>
                    ) : canMarkAsUnpaid ? (
                      <ListItem key="Unpay" disablePadding>
                        <ListItemButton
                          sx={{
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                          onClick={() => {
                            openDialog({
                              theme: theme,
                              title: "Mark As Unpaid",
                              message:
                                "This assignment will be marked as Unpaid and returned to not invoiced status, are you sure you want to continue?",
                              customContent: null,

                              tertiaryAction: null,
                              secondaryAction: "Close",
                              primaryAction: "Confirm",

                              tertiaryOnClick: null,

                              secondaryOnClick: () => {
                                closeDialog();
                              },

                              primaryOnClick: () => {
                                markAssignmentAsUnpaid();
                                closeDialog();
                              },
                            });
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <RestartAltRoundedIcon
                              sx={{
                                fontSize: 28,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Mark As Unpaid" />
                        </ListItemButton>
                      </ListItem>
                    ) : null}

                    <ListItem key="ChangeCategory" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          const secondData = {
                            ServiceID: translationData.id,
                            Category: translationData.ServiceCategory,
                          };

                          setOverlayData(secondData);

                          setOverlayName(OVERLAY_NAMES.SERVICE_CHANGE_CATEGORY);
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <CategoryRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Change Category" />
                      </ListItemButton>
                    </ListItem>

                    <ListItem key="DeleteAssignment" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          openDialog({
                            theme: theme,
                            title: "Delete Assignment",
                            message:
                              "Do you really wish to delete this assignment?",
                            customContent: null,

                            tertiaryAction: null,
                            secondaryAction: "Close",
                            primaryAction: "Delete",

                            tertiaryOnClick: null,

                            secondaryOnClick: () => {
                              closeDialog();
                            },

                            primaryOnClick: () => {
                              deleteService();
                              closeDialog();
                            },
                          });
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <DeleteRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Delete Assignment" />
                      </ListItemButton>
                    </ListItem>
                  </Box>

                  <Divider
                    sx={{
                      my: 2,
                    }}
                  />

                  <Box width="100%">
                    <Typography
                      sx={{
                        m: "0.25rem 0 0 0.5rem",
                        color: theme.palette.primary.main,
                        fontSize: 13,
                      }}
                    >
                      Tasks & Notes
                    </Typography>

                    <ListItem key="AddTask" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          setOverlayData({
                            Action: OVERLAY_NAMES.TASK_ACTION_NEW,
                            Task: {
                              EntityType: "Service",
                              EntityID: id,
                              ServiceType: "Translation",
                            },
                            Service: {
                              id: translationData.id,
                              ProjectNumber: translationData.ProjectNumber,
                              Details: translationData.Details,
                              ServiceCategory: translationData.ServiceCategory,
                            },
                          });
                          setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <TaskRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Add Task" />
                      </ListItemButton>
                    </ListItem>

                    <ListItem key="AddNote" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          const data = {
                            ServiceID: translationData.id,
                            MemberID: currentUser.id,
                            ModelType: "Translation",
                            Action: OVERLAY_NAMES.SERVICE_NOTE_ACTION_NEW,
                          };

                          setOverlayData(data);

                          setOverlayName(OVERLAY_NAMES.SERVICE_NOTE_NEW_EDIT);
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <CommentRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Add Note" />
                      </ListItemButton>
                    </ListItem>

                    <Divider
                      sx={{
                        my: 2,
                      }}
                    />

                    <Typography
                      sx={{
                        m: "0.25rem 0 0 0.5rem",
                        color: theme.palette.primary.main,
                        fontSize: 13,
                      }}
                    >
                      Utilities
                    </Typography>

                    <ListItem key="CopyTrackerRow" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={handleCopyTrackerRow}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <ContentCopyRoundedIcon
                            sx={{
                              fontSize: 24,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Copy Tracker Row" />
                      </ListItemButton>
                    </ListItem>

                    <ListItem key="OneDrive" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          const year = dayjs(translationData.Date).format(
                            "YYYY",
                          );

                          const oneDriveLink =
                            "https://communicationessentialsllc-my.sharepoint.com/personal/amer_communicationessentialsllc_com/_layouts/15/onedrive.aspx?id=%2Fpersonal%2Famer%5Fcommunicationessentialsllc%5Fcom%2FDocuments%2FCommunication%20Essentials%2FAssignments%2FTranslations%20" +
                            year +
                            "%2F" +
                            translationData.ProjectNumber;

                          window.open(oneDriveLink, "_blank");
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <CloudIcon
                            sx={{
                              fontSize: 24,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Open One Drive Link" />
                      </ListItemButton>
                    </ListItem>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default TranslationProfile;
